import { useCallback, useEffect, useRef, useState } from "react";
import { Barcode, CheckCircle2, PackageCheck, ScanLine } from "lucide-react";
import { ApiError } from "@/api/client";
import { preparationsService } from "@/api/services";
import { EmptyState } from "@/components/common/EmptyState";
import { ErrorState } from "@/components/common/ErrorState";
import { LoadingState } from "@/components/common/LoadingState";
import { PageHeader } from "@/components/common/PageHeader";
import { StatusBadge } from "@/components/common/StatusBadge";
import { construireScan, creerCleScan, executerScan } from "@/features/preparations/picking";
import { ExpeditionPanel } from "@/features/expeditions/ExpeditionPanel";
import type { CommandeEligiblePreparation, PreparationDetail, PreparationEnCours, ScanPreparationPayload } from "@/types/domain";
import { useAuth } from "@/auth/AuthProvider";

const messageErreur = (erreur: unknown) => {
  if (erreur instanceof ApiError && erreur.detail && typeof erreur.detail === "object") {
    return (erreur.detail as { message?: string }).message || erreur.message;
  }
  return erreur instanceof Error ? erreur.message : "Opération impossible.";
};

export default function ExpeditionPage() {
  const { hasAnyPermission } = useAuth();
  const peutPreparer = hasAnyPermission("preparations.manage");
  const [onglet, setOnglet] = useState<"preparation" | "expedition">("preparation");
  const scannerRef = useRef<HTMLInputElement>(null);
  const [eligibles, setEligibles] = useState<CommandeEligiblePreparation[]>([]);
  const [enCours, setEnCours] = useState<PreparationEnCours[]>([]);
  const [preparation, setPreparation] = useState<PreparationDetail | null>(null);
  const [ligneId, setLigneId] = useState("");
  const [identifiant, setIdentifiant] = useState("");
  const [quantite, setQuantite] = useState(1);
  const [pending, setPending] = useState<ScanPreparationPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scanError, setScanError] = useState<string | null>(null);

  const chargerFile = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [commandes, preparations] = await Promise.all([
        preparationsService.eligibles(), preparationsService.enCours(),
      ]);
      setEligibles(commandes); setEnCours(preparations);
    } catch (erreur) { setError(messageErreur(erreur)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { void chargerFile(); }, [chargerFile]);

  const afficherPreparation = (detail: PreparationDetail) => {
    setPreparation(detail);
    const active = detail.lignes.find((l) => l.quantite_preparee < l.quantite_attendue);
    setLigneId(active?.id || detail.lignes[0]?.id || "");
    setTimeout(() => scannerRef.current?.focus(), 0);
  };

  const creer = async (commandeId: string) => {
    setBusy(true); setError(null);
    try { afficherPreparation(await preparationsService.creer(commandeId)); await chargerFile(); }
    catch (erreur) { setError(messageErreur(erreur)); }
    finally { setBusy(false); }
  };

  const ouvrir = async (preparationId: string) => {
    setBusy(true); setError(null);
    try { afficherPreparation(await preparationsService.detail(preparationId)); }
    catch (erreur) { setError(messageErreur(erreur)); }
    finally { setBusy(false); }
  };

  const envoyerScan = async (payload: ScanPreparationPayload) => {
    if (!preparation) return;
    setBusy(true); setScanError(null);
    try {
      const resultat = await executerScan(payload, (scan) => preparationsService.scanner(preparation.id, scan));
      setPending(null); setIdentifiant(""); setQuantite(1);
      afficherPreparation(resultat.preparation); await chargerFile();
    } catch (erreur) {
      setPending(payload);
      setScanError(`${messageErreur(erreur)} Aucun fallback local n'a été appliqué.`);
    } finally { setBusy(false); setTimeout(() => scannerRef.current?.focus(), 0); }
  };

  const scanner = (event: React.FormEvent) => {
    event.preventDefault();
    if (!preparation || pending) return;
    const ligne = preparation.lignes.find((item) => item.id === ligneId);
    if (!ligne) { setScanError("Sélectionnez une ligne de préparation."); return; }
    try {
      void envoyerScan(construireScan(ligne, identifiant, quantite, creerCleScan(ligne.id)));
    } catch (erreur) { setScanError(messageErreur(erreur)); }
  };

  const finaliser = async () => {
    if (!preparation) return;
    setBusy(true); setScanError(null);
    try { afficherPreparation(await preparationsService.finaliser(preparation.id)); await chargerFile(); }
    catch (erreur) { setScanError(messageErreur(erreur)); }
    finally { setBusy(false); }
  };

  const selection = preparation?.lignes.find((ligne) => ligne.id === ligneId);
  const progression = preparation
    ? Math.round(preparation.progression.quantite_preparee / Math.max(1, preparation.progression.quantite_attendue) * 100)
    : 0;

  return (
    <div className="space-y-6" data-testid="expedition-page">
      <PageHeader title="Préparation et expédition" subtitle="Deux étapes distinctes : préparer sans déduire, puis confirmer l'expédition réelle." breadcrumb={["Opérations", "Préparation / Expédition"]} />

      <div className="inline-flex rounded-sm border border-border bg-card p-1" role="tablist" aria-label="Étape opérationnelle">
        <button type="button" role="tab" aria-selected={onglet === "preparation"} onClick={() => setOnglet("preparation")} className={`ops-focus rounded-sm px-4 py-2 text-sm font-semibold ${onglet === "preparation" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}>Préparation</button>
        <button type="button" role="tab" aria-selected={onglet === "expedition"} onClick={() => setOnglet("expedition")} className={`ops-focus rounded-sm px-4 py-2 text-sm font-semibold ${onglet === "expedition" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}>Expédition</button>
      </div>

      {onglet === "preparation" && <>

      {loading ? <LoadingState label="Chargement de la file..." rows={4} /> : error ? (
        <ErrorState title="File indisponible" description={error} onRetry={() => void chargerFile()} />
      ) : (
        <section className="ops-panel p-4" data-testid="preparations-file">
          <div className="mb-4 flex items-center gap-2"><PackageCheck className="h-5 w-5" /><h2 className="text-base font-semibold">File de préparation</h2></div>
          {enCours.length === 0 && eligibles.length === 0 ? (
            <EmptyState title="Aucune commande à préparer" description="Seules les commandes entièrement réservées apparaissent ici." />
          ) : <div className="grid gap-3 lg:grid-cols-2">
            {enCours.map((item) => <button key={item.preparation_id} type="button" disabled={busy} onClick={() => void ouvrir(item.preparation_id)} className="ops-focus rounded-sm border border-blue-300 bg-blue-50 p-4 text-left dark:bg-blue-500/10">
              <div className="flex justify-between gap-3"><strong>{item.numero_commande}</strong><StatusBadge status="EN_PREPARATION" /></div>
              <p className="mt-1 text-sm">{item.client} · PO {item.numero_po_client}</p><p className="mt-2 font-mono text-xs">{item.quantite_preparee} / {item.quantite_attendue} articles</p>
            </button>)}
            {peutPreparer && eligibles.map((item) => <button key={item.commande_id} type="button" disabled={busy} onClick={() => void creer(item.commande_id)} className="ops-focus rounded-sm border border-border p-4 text-left hover:bg-muted">
              <div className="flex justify-between gap-3"><strong>{item.numero_commande}</strong><StatusBadge status="RESERVE" /></div>
              <p className="mt-1 text-sm">{item.client} · PO {item.numero_po_client}</p><p className="mt-2 font-mono text-xs">{item.nombre_lignes} ligne(s) · {item.quantite_a_preparer} articles</p>
            </button>)}
          </div>}
        </section>
      )}

      {preparation && <section className="ops-panel space-y-5 p-5" data-testid="preparation-detail">
        <div className="flex flex-wrap items-start justify-between gap-3"><div><p className="ops-label">Commande</p><h2 className="text-xl font-semibold">{preparation.commande.numero_commande}</h2><p className="text-sm text-muted-foreground">{preparation.commande.client} · PO {preparation.commande.numero_po_client}</p></div><StatusBadge status={preparation.commande.statut} /></div>
        <div><div className="mb-1 flex justify-between text-sm"><span>Progression</span><strong>{preparation.progression.quantite_preparee} / {preparation.progression.quantite_attendue}</strong></div><div className="h-3 overflow-hidden rounded-full bg-muted"><div className="h-full bg-emerald-500 transition-all" style={{ width: `${progression}%` }} /></div></div>

        <div className="grid gap-3">{preparation.lignes.map((ligne) => <button key={ligne.id} type="button" onClick={() => { setLigneId(ligne.id); scannerRef.current?.focus(); }} className={`ops-focus grid gap-2 rounded-sm border p-4 text-left md:grid-cols-[1fr_auto] ${ligne.id === ligneId ? "border-accent ring-2 ring-accent/30" : "border-border"}`} data-testid={`preparation-line-${ligne.id}`}>
          <div><p className="font-mono font-semibold">{ligne.sku}</p><p>{ligne.description}</p><p className="mt-1 text-sm text-muted-foreground">Emplacement : <strong>{ligne.emplacement || "Non défini"}</strong></p></div>
          <div className="text-right"><p className="ops-label">Préparée / attendue</p><p className="font-mono text-xl font-semibold">{ligne.quantite_preparee} / {ligne.quantite_attendue}</p><p className="text-xs text-muted-foreground">Écart : {ligne.ecart}</p></div>
        </button>)}</div>

        {peutPreparer && preparation.statut === "EN_COURS" && <form onSubmit={scanner} className="rounded-sm border border-border bg-muted/30 p-4" data-testid="picking-scanner">
          <div className="mb-3 flex items-center gap-2"><Barcode className="h-5 w-5" /><h3 className="font-semibold">Scanner la ligne sélectionnée</h3></div>
          <p className="mb-3 text-sm">{selection ? `${selection.sku} — restant ${selection.ecart}` : "Sélectionnez une ligne"}</p>
          <div className="flex flex-col gap-3 sm:flex-row"><div className="relative flex-1"><ScanLine className="absolute left-3 top-3 h-5 w-5 text-muted-foreground" /><input ref={scannerRef} autoFocus autoComplete="off" value={identifiant} disabled={busy || Boolean(pending)} onChange={(e) => setIdentifiant(e.target.value)} placeholder="Scanner puis Enter" className="ops-focus h-11 w-full rounded-sm border bg-background pl-11 pr-3 font-mono" data-testid="picking-scan-input" /></div><input type="number" min="1" step="1" value={quantite} disabled={busy || Boolean(pending)} onChange={(e) => setQuantite(Number(e.target.value))} className="ops-focus h-11 w-24 rounded-sm border bg-background px-3 text-right font-mono" aria-label="Quantité" /><button type="submit" disabled={busy || Boolean(pending)} className="ops-focus h-11 rounded-sm bg-primary px-5 font-semibold text-primary-foreground">Valider le scan</button></div>
          {scanError && <div className="mt-3 rounded-sm border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive" role="alert">{scanError}{pending && <button type="button" disabled={busy} onClick={() => void envoyerScan(pending)} className="ops-focus ml-3 rounded-sm border px-3 py-1 font-semibold">Réessayer le même événement</button>}</div>}
        </form>}

        {preparation.statut === "FINALISEE" ? <div className="flex items-center gap-2 rounded-sm border border-emerald-300 bg-emerald-50 p-4 text-emerald-900 dark:bg-emerald-500/10"><CheckCircle2 className="h-5 w-5" /><strong>Préparation confirmée — commande PRET</strong></div> : peutPreparer && <button type="button" disabled={busy || !preparation.progression.complete || Boolean(pending)} onClick={() => void finaliser()} className="ops-focus rounded-sm bg-emerald-700 px-5 py-3 font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50" data-testid="finalize-preparation">Confirmer la préparation complète</button>}
      </section>}
      </>}

      {onglet === "expedition" && <ExpeditionPanel />}
    </div>
  );
}
